import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Activity, Upload, Sparkles, Loader2, Trash2, AlertTriangle, Eye, CheckCircle2 } from 'lucide-react';

import { API_URL } from '../config';

export default function ECGAIPage() {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [ecgImage, setEcgImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Selectionner une image');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      setEcgImage(e.target.result);
      setAiAnalysis(null);
      toast.success('ECG charge');
    };
    reader.readAsDataURL(file);
  };

  const handleAnalyze = async () => {
    if (!ecgImage) return;
    try {
      setIsAnalyzing(true);
      toast.info('Analyse...');
      const response = await fetch(`${API_URL}/api/ai/analyze-ecg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ imageData: ecgImage })
      });
      if (!response.ok) throw new Error('Erreur');
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('OK');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="p-3 md:p-6 space-y-3 max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-6 h-6 text-cyan-400" />
        <h1 className="text-lg md:text-2xl font-bold text-white">ECG AI</h1>
      </div>

      <Tabs defaultValue="upload">
        <TabsList className="grid w-full grid-cols-3 bg-gray-800">
          <TabsTrigger value="upload">Upload ECG</TabsTrigger>
          <TabsTrigger value="aide">Aide ECG</TabsTrigger>
          <TabsTrigger value="memo">Memo Rythme</TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="mt-3">
          {!imagePreview ? (
            <Card className="border-cyan-500/30">
              <CardContent className="p-8">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" id="ecg-up" />
                <label htmlFor="ecg-up" className="flex flex-col items-center cursor-pointer">
                  <div className="w-16 h-16 rounded-full bg-cyan-400/20 flex items-center justify-center mb-4">
                    <Upload className="w-8 h-8 text-cyan-400" />
                  </div>
                  <p className="text-sm text-white mb-1">Charger un ECG</p>
                  <p className="text-xs text-gray-400">JPG, PNG</p>
                </label>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              <Card>
                <CardHeader className="p-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm text-cyan-300">ECG Charge</CardTitle>
                  <div className="flex gap-2">
                    <Button onClick={handleAnalyze} disabled={isAnalyzing} size="sm">
                      {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    </Button>
                    <Button onClick={() => { setEcgImage(null); setImagePreview(null); setAiAnalysis(null); }} size="sm" variant="outline">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-3">
                  <img src={imagePreview} alt="ECG" className="w-full rounded" />
                </CardContent>
              </Card>

              {aiAnalysis && (
                <Card className="border-purple-500/30">
                  <CardHeader className="p-3">
                    <CardTitle className="text-sm text-purple-300">ANALYSE IA</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3">
                    <div className="whitespace-pre-wrap text-xs text-white">{aiAnalysis.analysis}</div>
                    {aiAnalysis.urgence && (
                      <Alert className={`mt-3 ${aiAnalysis.urgence === 'CRITIQUE' ? 'bg-red-500/20' : aiAnalysis.urgence === 'URGENT' ? 'bg-amber-500/20' : 'bg-green-500/20'} p-2`}>
                        <AlertDescription className="text-xs font-bold">URGENCE: {aiAnalysis.urgence}</AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="aide" className="mt-3 space-y-3">
          <Card className="border-cyan-500/30">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-cyan-300">VALEURS NORMALES</CardTitle>
            </CardHeader>
            <CardContent className="p-3 text-xs text-gray-300 space-y-1">
              <div>FC: 60-100 bpm</div>
              <div>PR: 120-200 ms</div>
              <div>QRS: &lt; 120 ms</div>
              <div>QT: &lt; 440 ms (H) / &lt; 460 ms (F)</div>
            </CardContent>
          </Card>

          <Card className="border-purple-500/30">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-purple-300">RYTHMES IRREGULIERS</CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <table className="w-full text-xs text-gray-300">
                <tbody>
                  <tr className="border-b border-gray-700"><td className="p-2">Rapide + P absente</td><td className="p-2 text-purple-300">FA</td></tr>
                  <tr className="border-b border-gray-700"><td className="p-2">Onde F (dents scie)</td><td className="p-2 text-purple-300">Flutter</td></tr>
                  <tr className="border-b border-gray-700"><td className="p-2">P et QRS absents</td><td className="p-2 text-red-300">FV</td></tr>
                  <tr><td className="p-2">QRS large &gt; 120ms</td><td className="p-2 text-red-300">TV</td></tr>
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card className="border-green-500/30">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-green-300">BLOCS AV</CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <table className="w-full text-xs text-gray-300">
                <tbody>
                  <tr className="border-b border-gray-700"><td className="p-2">PR &gt; 200ms constant</td><td className="p-2 text-amber-300">BAV I</td></tr>
                  <tr className="border-b border-gray-700"><td className="p-2">PR puis 1P sans QRS</td><td className="p-2 text-amber-300">BAV II Mobitz I</td></tr>
                  <tr><td className="p-2">P et QRS independants</td><td className="p-2 text-red-300">BAV III</td></tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="memo" className="mt-3 space-y-3">
          <Alert className="bg-cyan-500/10 border-cyan-500/30 p-3">
            <AlertDescription className="text-xs font-bold text-cyan-300">
              MEMO ULTRA-SYNTHESE - Troubles de la conduction (urgentistes)<br/>
              Format terrain - Lecture &lt; 1 min
            </AlertDescription>
          </Alert>

          <Card className="border-blue-500/30">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-blue-300">BLOC SINO-AURICULAIRE (BSA)</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-3 text-xs">
              <div>
                <div className="font-semibold text-blue-300 mb-1">BSA I</div>
                <div className="text-gray-300 pl-3">• ECG: P-P constants<br/>• Clinique: silencieux<br/>⚠️ non detectable</div>
              </div>
              <div>
                <div className="font-semibold text-blue-300 mb-2">BSA II</div>
                <div className="space-y-2 pl-3">
                  <div><strong className="text-cyan-300">Type I (Wenckebach sinusal):</strong> P-P s'allonge → pause</div>
                  <div><strong className="text-cyan-300">Type I decroissant:</strong> P-P raccourcit → pause</div>
                  <div><strong className="text-cyan-300">Type II:</strong> P-P constants + 1 pause brutale</div>
                  <div><strong className="text-cyan-300">Haut degre:</strong> P-P constants + ≥ 2 pauses</div>
                  <div className="text-amber-300 mt-1">➡️ Risque syncopal si pauses prolongees</div>
                </div>
              </div>
              <div>
                <div className="font-semibold text-blue-300 mb-1">BSA III</div>
                <div className="text-gray-300 pl-3">• P absentes<br/>• Rythme echappement<br/><span className="text-red-300">🚨 URGENCE si mal tolere</span></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-500/30">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-red-300">BLOC AURICULO-VENTRICULAIRE (BAV)</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-3 text-xs">
              <div>
                <div className="font-semibold text-amber-300 mb-1">BAV I</div>
                <div className="text-gray-300 pl-3">• PR ≥ 200 ms, constant<br/>⚠️ benin isole</div>
              </div>
              <div>
                <div className="font-semibold text-amber-300 mb-2">BAV II</div>
                <div className="space-y-2 pl-3">
                  <div><strong className="text-cyan-300">Mobitz I (Wenckebach):</strong> PR s'allonge → P bloquee<br/>Souvent nodal, mieux tolere</div>
                  <div><strong className="text-amber-300">BAV 2/1:</strong> 1 QRS sur 2, PR constant<br/>⚠️ indeterminable → prudence</div>
                  <div><strong className="text-red-300">Mobitz II:</strong> PR constant, P bloquees imprevisi bles<br/>🚨 GRAVE → pacemaker</div>
                </div>
              </div>
              <div>
                <div className="font-semibold text-red-300 mb-1">BAV haut degre</div>
                <div className="text-gray-300 pl-3">• ≥ 2 P bloquees consecutives<br/><span className="text-red-300">🚨 Risque asystolie</span></div>
              </div>
              <div>
                <div className="font-semibold text-red-300 mb-1">BAV III (complet)</div>
                <div className="text-gray-300 pl-3">• Dissociation P / QRS<br/>• Rythme echappement<br/><span className="text-red-300 font-bold">🚨 URGENCE VITALE</span></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-500/30">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-green-300">CONDUITE AUX URGENCES</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-3 text-xs">
              <div>
                <div className="font-semibold text-amber-300 mb-2">⚠️ Signes de gravite</div>
                <div className="text-gray-300 pl-3 space-y-1">
                  <div>• Syncope / lipothymie</div>
                  <div>• Bradycardie &lt; 40/min</div>
                  <div>• Hypotension, OAP, douleur thoracique</div>
                  <div>• Pause &gt; 3s (eveil) ou &gt; 5s (sommeil)</div>
                </div>
              </div>
              <div>
                <div className="font-semibold text-green-300 mb-2">🔌 Prise en charge immediate</div>
                <div className="text-gray-300 pl-3 space-y-1">
                  <div>1. Scope + O₂ + VVP</div>
                  <div>2. Atropine IV (si BAV nodal probable)</div>
                  <div>3. Isoprenaline si echec</div>
                  <div>4. Pacing externe si instabilite</div>
                  <div>5. Avis cardio / USIC</div>
                </div>
              </div>
              <Alert className="bg-purple-500/20 border-purple-500/40 p-3">
                <AlertDescription className="text-xs">
                  <strong className="text-purple-300">🧠 Perle urgentiste:</strong><br/>
                  <span className="text-white font-medium">Mobitz II, BAV 2/1 et BAV III = pacemaker jusqu'a preuve du contraire</span>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
